import type { SerializedStyles } from "@emotion/serialize";
import { Typography } from "@fullscript/aviary-web";
import { useTranslation } from "react-i18next";

import { l } from "@shared/locales/i18n";
import type { PatientAdditionalAttributesType } from "@shared/types/PatientAdditionalAttributesType";
import type { PractitionerAdditionalAttributesType } from "@shared/types/PractitionerAdditionalAttributesType";
import type { SignInAdditionalAttributesType } from "@shared/types/SignInAdditionalAttributesType";

import * as layoutStyles from "../loginLayout.styles";
import { getActionURL } from "../utils/getActionURL";

import AppleSVGComponent from "./assets/Apple";

import * as styles from "./AppleSSOButton.styles";

interface Props {
  formData?: {
    firstName?: string;
    lastName?: string;
    storeName?: string;
    tosAcceptedAt?: string;
  };
  treatmentPlanSlug?: string;
  labRecommendationId?: string;
  storeSlug?: string;
  pracTypeId?: string;
  signUpPage?: string;
  pageVersion?: string;
  disabled?: boolean;
  isOrTextRendered?: boolean;
  additionalAttributes?: SignInAdditionalAttributesType &
    PractitionerAdditionalAttributesType &
    PatientAdditionalAttributesType;
  uuid?: string;
  buttonStyles?: SerializedStyles;
  isIntake?: boolean;
  requestedPractitionerId?: string;
  className?: string;
  isLastUsedEnabled?: boolean;
}

const AppleSSOButton = ({
  formData,
  signUpPage,
  treatmentPlanSlug,
  labRecommendationId,
  storeSlug,
  pracTypeId,
  pageVersion,
  disabled,
  additionalAttributes,
  uuid,
  isOrTextRendered = false,
  buttonStyles,
  isIntake,
  requestedPractitionerId,
  className,
  isLastUsedEnabled,
  ...rest
}: Props) => {
  const { t } = useTranslation();
  const csrf = document.querySelector("meta[name='csrf-token']")?.getAttribute("content") ?? "";
  const buttonText = t(l.appleSSO.ContinueWithApple);
  const submitInputId = "apple-sso-submit";

  const formStyles = [
    styles.form,
    isLastUsedEnabled && layoutStyles.fullWidth,
    isLastUsedEnabled && layoutStyles.buttonHeight,
    disabled && styles.formDisabled,
    buttonStyles,
    !isLastUsedEnabled && styles.formDS4,
  ];
  const submitStyles = [
    styles.submit,
    disabled && styles.submitDisabled,
    !isLastUsedEnabled && styles.submitDS4,
  ];
  const logoStyles = [styles.logo, disabled && styles.disabled];
  const appleSSOButtonStyles = [
    styles.AppleSSOButton,
    isLastUsedEnabled && layoutStyles.fullWidth,
    disabled && styles.disabled,
  ];

  return (
    <div css={styles.buttonWrapper} className={className}>
      <div css={formStyles} {...rest}>
        <form
          action={getActionURL(
            "Apple",
            formData,
            additionalAttributes,
            signUpPage,
            treatmentPlanSlug,
            labRecommendationId,
            storeSlug,
            pracTypeId,
            pageVersion,
            uuid,
            isIntake,
            requestedPractitionerId
          )}
          method="post"
          data-e2e="apple-sso-button"
        >
          <label aria-label={buttonText} css={appleSSOButtonStyles} htmlFor={submitInputId}>
            <AppleSVGComponent css={logoStyles} />
            <input
              id={submitInputId}
              value={buttonText}
              type="submit"
              css={submitStyles}
              disabled={disabled}
            />
          </label>
          <input type="hidden" name="authenticity_token" value={csrf}></input>
        </form>
      </div>
      {!!isOrTextRendered && (
        <Typography
          variant="footnote"
          isMarginless
          customCss={styles.orSection}
          intention="neutral"
          colorIntensity="subtle"
        >
          {t(l.appleSSO.Or)}
        </Typography>
      )}
    </div>
  );
};

export { AppleSSOButton };
